import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { Alert, DeviceEventEmitter, Platform } from 'react-native';
import config from '../config';
import { secureStorage } from './secureStorage';

let isRefreshing = false;
let failedQueue: { resolve: (v: any) => void; reject: (e: any) => void }[] = [];
let suspensionAlertShown = false;

export const notifyAccountSuspended = async (message = 'Your account has been suspended by admin. Contact support to restore access.') => {
  await secureStorage.removeItem('tokens');
  if (suspensionAlertShown) return;
  suspensionAlertShown = true;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('survey-connect:account-suspended', { detail: { message } }));
    window.alert(message);
    window.location.href = '/login';
    return;
  }
  DeviceEventEmitter.emit('survey-connect:account-suspended', { message });
  Alert.alert('Account suspended', message);
};

const isSuspensionError = (error: any) => {
  const detail = error?.response?.data?.detail || error?.message || '';
  return String(detail).toLowerCase().includes('suspended');
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.API_BASE_URL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  // ── Request interceptor: attach JWT ──
  client.interceptors.request.use(async (cfg: InternalAxiosRequestConfig) => {
    try {
      const raw = await secureStorage.getItem('tokens');
      if (raw) {
        const { access } = JSON.parse(raw);
        if (access) cfg.headers.Authorization = `Bearer ${access}`;
      }
    } catch {}
    return cfg;
  });

  // ── Response interceptor: refresh on 401 ──
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      if (isSuspensionError(error)) {
        await notifyAccountSuspended((error.response?.data as any)?.detail);
        return Promise.reject(error);
      }
      if (error.response?.status === 401 && !original._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return client(original);
          });
        }
        original._retry = true;
        isRefreshing = true;
        try {
          const raw = await secureStorage.getItem('tokens');
          if (!raw) throw new Error('No tokens');
          const { refresh } = JSON.parse(raw);
          const { data } = await axios.post(`${config.API_BASE_URL}/auth/refresh/`, { refresh });
          await secureStorage.setItem('tokens', JSON.stringify({ access: data.access, refresh }));
          processQueue(null, data.access);
          original.headers.Authorization = `Bearer ${data.access}`;
          return client(original);
        } catch (e) {
          processQueue(e, null);
          await secureStorage.removeItem('tokens');
          if (isSuspensionError(e) || isSuspensionError(error)) {
            await notifyAccountSuspended((error.response?.data as any)?.detail);
            return Promise.reject(e);
          }
          if (typeof window !== 'undefined' && window.location) {
            window.location.href = '/login';
          }
          return Promise.reject(e);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    },
  );

  return client;
};

export const apiClient = createApiClient();

const firstString = (value: any): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return firstString(value[0]);
  if (value && typeof value === 'object') {
    const key = Object.keys(value)[0];
    return key ? firstString(value[key]) : null;
  }
  return null;
};

// Generic error extractor
export const extractApiError = (error: any): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data?.detail) return data.detail;
    if (data?.message) return data.message;
    if (data?.non_field_errors) {
      const message = firstString(data.non_field_errors);
      if (message) return message;
    }
    if (data?.errors) {
      const message = firstString(data.errors);
      if (message) return message;
    }
    if (error.response?.status === 500) return 'Server error. Please try again later.';
    if (error.response?.status === 403) return 'You do not have permission for this action.';
    if (error.response?.status === 404) return 'Resource not found.';
    const fieldMessage = firstString(data);
    if (fieldMessage) {
      return fieldMessage;
    }
    if (error.response?.status === 400) {
      return 'This request could not be completed. Please check the details and try again.';
    }
    if (error.request && !error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'The connection timed out. Check your internet connection and try again.';
      }
      return 'We could not reach Survey Connect. Check your internet connection and try again.';
    }
  }
  if (error?.message && !String(error.message).startsWith('Request failed with status code')) return error.message;
  return 'An unexpected error occurred.';
};
